import React, { SyntheticEvent, useCallback, useEffect, useState } from "react";
import { Box, TextField, Autocomplete, InputAdornment } from "@mui/material";
import { IFoundPlaces, ISearchAddressInputProps } from "./types";
import { v4 as uuidv4 } from "uuid";
import styles from "./SearchAddressInput.styled";

const SearchAddressInput: React.FC<ISearchAddressInputProps> = ({
  data,
  initialValue,
  onSearch,
  onSelectValue,
  sx,
  label,
  disabled,
  name,
  endAdornment: EndAdornment,
  onIconClick,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState<IFoundPlaces | null>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    if (initialValue) {
      setInputValue(initialValue);
    } else {
      setInputValue("");
    }
  }, [initialValue]);

  useEffect(() => {
    onSelectValue(selectedValue);
  }, [selectedValue]);

  const searchStyles = {
    ...styles.autocompleteInput,
    ...sx,
  };

  const handleSelectChange = useCallback(
    (event: SyntheticEvent, value: IFoundPlaces | null): void => {
      setSelectedValue(value);
      value && setInputValue(value.name);
    },
    [name],
  );

  const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onSearch(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <Autocomplete
      sx={searchStyles}
      options={data}
      getOptionLabel={(option) => option.name}
      filterOptions={(options) => options}
      inputValue={inputValue}
      value={selectedValue}
      onChange={handleSelectChange}
      clearIcon={null}
      disabled={disabled}
      open={isFocused && !!data.length && !selectedValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onChange={handleInputChanged}
          InputProps={{
            ...params.InputProps,
            ...(EndAdornment && {
              endAdornment: (
                <InputAdornment position="end">
                  <div
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onIconClick?.(event);
                    }}
                  >
                    <EndAdornment sx={styles.visibleIcon} />
                  </div>
                </InputAdornment>
              ),
            }),
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <Box {...props} component="li" sx={styles.option} key={uuidv4()}>
            {option.name}
          </Box>
        );
      }}
    />
  );
};

export default SearchAddressInput;
